import React, { useEffect, useState } from 'react';
import { LifeBuoy } from 'lucide-react';
import Button from '../shared/Button';
import supportTicketService from '../../services/supportTicketService';
import { apiErrorMessage } from '../../utils/apiError';
import './SupportDeskButton.css';

// A CHAIN OR HOTEL ON THE SUPPORT DESK (V671) — the HCS Service Management desk keeps a chain as an organization and a
// hotel as a component, and every ticket raised in hub about them carries both. The partner's admins add them here:
//
//   entity   'chain' (chain settings) | 'hotel' (the hotel page)
//   id       the chain's or hotel's id
//
// Partner-only (teal): the server answers 404 to anyone who is not the partner's (or a superadmin), and then nothing is
// drawn. A hotel's button appears only once its chain is on the desk. Once added, a chip says so.

function SupportDeskButton({ entity, id }) {
    const [desk, setDesk] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        setDesk(null);
        setError(null);
        if (id) {
            supportTicketService.getDesk(entity, id)
                .then((res) => { if (live) setDesk(res.data); })
                .catch(() => { if (live) setDesk(null); });   // not the partner's — show nothing
        }
        return () => { live = false; };
    }, [entity, id]);

    if (!desk) return null;
    const noun = entity === 'chain' ? 'chain' : 'hotel';

    if (desk.linked) {
        return (
            <span className="sdb-chip partner-only" title={`Tickets about this ${noun} are filed under it on the support desk`}>
                <LifeBuoy className="sdb-icon" /> On the support desk
            </span>
        );
    }
    if (!desk.canLink || (entity === 'hotel' && !desk.chainLinked)) return null;

    const add = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await supportTicketService.addToDesk(entity, id);
            setDesk(res.data);
        } catch (err) {
            setError(apiErrorMessage(err, `Could not add the ${noun} to the support desk`));
        } finally {
            setBusy(false);
        }
    };

    return (
        <span className="sdb-wrap">
            <Button variant="secondary" className="partner-only" onClick={add} disabled={busy}
                title={entity === 'chain'
                    ? 'Create this chain as an organization on the support desk, so its tickets are filed under it'
                    : 'Add this hotel as a component on the support desk, so its tickets are filed under it'}>
                <LifeBuoy className="icon" /> {busy ? 'Adding…' : `Add ${noun} to support desk`}
            </Button>
            {error && <span className="sdb-error">{error}</span>}
        </span>
    );
}

export default SupportDeskButton;
